import {Component} from 'react';
import gql from 'graphql-tag';

import gqlClient from '../../graphql/client/gqlClient';
import Logger from '../../common/logger';
import {ENUMS} from '../../common';

const {CampaignContentWorkflow, ContentType} = ENUMS;
const {navigationMenu} = window.sn;

class ExtendedApprovalFlow extends Component {
    /**
     * Perform link content to campaign
     * Do not call both linkContentToCampaign and uploadContentToTikTokCSF
     * for the same piece of content, since uploadContentToTikTokCSF
     * links the content to the campaign too
     * @param {object} payload
     * @param {number} payload.sn_brand_id
     * @param {string} payload.sn_media_id
     * @param {number} payload.sn_campaign_id
     * @param {string} payload.sn_content_type
     * @param {string} campaignContentWorkflow
     * @returns {Promise<{wasSuccessful: boolean, error: any, transactionResult: any}>}
     */
    linkContentToCampaign = async (payload, campaignContentWorkflow) => {
        if (this.isUserAuthorisedToLinkContentOrUploadToCSF(campaignContentWorkflow)) {
            try {
                const transactionResult = await gqlClient.client.mutate({
                    mutation: gql`
                    mutation LinkCampaignContent($payload: JSON) {
                        sagaStart(
                            name: "LINK_CAMPAIGN_CONTENT"
                            version: "1"
                            payload: $payload
                        ) {
                            transactionId
                            wasSuccessful
                        }
                    }
                `,
                    variables: {
                        payload
                    }
                });

                if (
                    transactionResult
                    && transactionResult.data
                    && transactionResult.data.sagaStart
                    && transactionResult.data.sagaStart.wasSuccessful
                ) {
                    return {
                        wasSuccessful: true,
                        transactionResult: transactionResult.data.sagaStart
                    };
                }

                return {
                    wasSuccessful: false,
                    error: 'Linking is not successful.'
                };
            } catch (error) {
                Logger.error('Failed to link content to campaign', {
                    error: error,
                    payload: payload
                });

                return {
                    wasSuccessful: false,
                    error: error
                };
            }
        }

        return {
            wasSuccessful: false,
            error: 'User is not authorized to link content to campaign.'
        };
    }

    /**
     * Perform upload content to TikTok CSF
     * Do not call both linkContentToCampaign and uploadContentToTikTokCSF
     * for the same piece of content, since uploadContentToTikTokCSF
     * links the content to the campaign too
     * @param {object} payload
     * @param {number} payload.sn_media_id
     * @param {string} payload.sn_campaign_name
     * @param {number} payload.sn_brand_id
     * @param {number} payload.sn_campaign_id
     * @param {string} content_type
     * @param {string} campaignContentWorkflow
     * @returns {Promise<{wasSuccessful: boolean, error: any, transactionResult: any}>}
     */
    uploadContentToTikTokCSF = async (payload, content_type, campaignContentWorkflow) => {
        if (!this.isContentVideo(content_type)) {
            return {
                wasSuccessful: false,
                error: 'Content is not a video.'
            };
        }

        if (this.isUserAuthorisedToLinkContentOrUploadToCSF(campaignContentWorkflow)) {
            try {
                const transactionResult = await gqlClient.client.mutate({
                    mutation: gql`
                        mutation UploadContentToTikTokCSF($payload: JSON) {
                            sagaStart(
                                name: "TIKTOK_CSF_DISTRIBUTE_CONTENT"
                                version: "1"
                                payload: $payload
                            ) {
                                transactionId
                                wasSuccessful
                            }
                        }
                    `,
                    variables: {
                        payload
                    }
                });

                if (
                    transactionResult
                    && transactionResult.data
                    && transactionResult.data.sagaStart
                    && transactionResult.data.sagaStart.wasSuccessful
                ) {
                    return {
                        wasSuccessful: true,
                        transactionResult: transactionResult.data.sagaStart
                    };
                }

                return {
                    wasSuccessful: false,
                    error: 'Upload is not successful.'
                }
            } catch (error) {
                Logger.error('Failed to upload content to TikTok CSF', {
                    error: error,
                    payload: payload
                });

                return {
                    wasSuccessful: false,
                    error: error
                }
            }
        }

        return {
            wasSuccessful: false,
            error: 'User is not authorized to upload content to TikTok CSF.'
        };
    }

    /**
     * Check if content is video
     * @param {string} content_type
     * @returns {boolean}
     */
    isContentVideo = (content_type) => {
        return content_type === ContentType.VIDEO;
    }

    /**
     * Check if campaign content workflow is OPS_AND_BRAND_PREAPPROVAL
     * @param {string} campaignContentWorkflow
     * @returns {boolean}
     */
    isCampaignContentWorkflowOpsPlusBrandPreapproval = (campaignContentWorkflow) => {
        return campaignContentWorkflow === CampaignContentWorkflow.OPS_AND_BRAND_PREAPPROVAL;
    }

    /**
     * Check if a user is authorised to link content to campaign or upload to TikTok CSF
     * @param {string} campaignContentWorkflow
     * @returns {boolean}
     */
    isUserAuthorisedToLinkContentOrUploadToCSF = (campaignContentWorkflow) => {
        const {isAdmin, isBrand, isAgency} = navigationMenu;

        return !!(
            // if the campaign workflow is OPS_AND_BRAND_PREAPPROVAL,
            // then the user must have a brand or agency role
            (
                this.isCampaignContentWorkflowOpsPlusBrandPreapproval(campaignContentWorkflow)
                && (isBrand || isAgency)
            )
            // for all other workflows, the user must be an admin
            || (
                !this.isCampaignContentWorkflowOpsPlusBrandPreapproval(campaignContentWorkflow)
                && isAdmin
            )
        );
    }
}

export default ExtendedApprovalFlow;



// WEBPACK FOOTER //
// ./approval_queues/components/ExtendedApprovalFlow.js